import { useState } from 'react'
import {
  Bold,
  Italic,
  Underline,
  Link as LinkIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Image as ImageIcon,
  Video,
  Youtube,
  ChevronDown,
} from 'lucide-react'
import styles from './LexicalToolbar.module.css'

export default function LexicalToolbar({
  isBold,
  isItalic,
  isUnderline,
  isLink,
  blockType,
  textColor,
  alignment,
  onFormatText,
  onFormatHeading,
  onFormatList,
  onInsertLink,
  onSetColor,
  onSetAlignment,
  onInsertImage,
  onInsertVideo,
  onInsertYouTube,
}) {
  const [showHeadingMenu, setShowHeadingMenu] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)

  const headingOptions = [
    { value: 'paragraph', label: 'Paragraph' },
    { value: 'h1', label: 'Heading 1' },
    { value: 'h2', label: 'Heading 2' },
    { value: 'h3', label: 'Heading 3' },
  ]

  const colors = [
    '#000000',
    '#333333',
    '#666666',
    '#999999',
    '#E53E3E',
    '#DD6B20',
    '#D69E2E',
    '#38A169',
    '#3182CE',
    '#5A67D8',
    '#805AD5',
    '#D53F8C',
  ]

  const getBlockTypeLabel = () => {
    const option = headingOptions.find((opt) => opt.value === blockType)
    return option ? option.label : 'Paragraph'
  }

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarGroup}>
        <div className={styles.dropdown}>
          <button
            className={styles.toolbarButton}
            onClick={() => setShowHeadingMenu(!showHeadingMenu)}
            type="button"
          >
            <span className={styles.blockTypeLabel}>{getBlockTypeLabel()}</span>
            <ChevronDown className="h-3 w-3" />
          </button>
          {showHeadingMenu && (
            <div className={styles.dropdownMenu}>
              {headingOptions.map((option) => (
                <button
                  key={option.value}
                  className={styles.dropdownItem}
                  onClick={() => {
                    onFormatHeading(option.value)
                    setShowHeadingMenu(false)
                  }}
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.divider} />

      <div className={styles.toolbarGroup}>
        <button
          className={`${styles.toolbarButton} ${isBold ? styles.active : ''}`}
          onClick={() => onFormatText('bold')}
          title="Bold"
          type="button"
        >
          <Bold className="h-4 w-4" />
        </button>
        <button
          className={`${styles.toolbarButton} ${isItalic ? styles.active : ''}`}
          onClick={() => onFormatText('italic')}
          title="Italic"
          type="button"
        >
          <Italic className="h-4 w-4" />
        </button>
        <button
          className={`${styles.toolbarButton} ${
            isUnderline ? styles.active : ''
          }`}
          onClick={() => onFormatText('underline')}
          title="Underline"
          type="button"
        >
          <Underline className="h-4 w-4" />
        </button>
      </div>

      <div className={styles.divider} />

      <div className={styles.toolbarGroup}>
        <button
          className={styles.toolbarButton}
          onClick={() => onFormatList('bullet')}
          title="Bullet List"
          type="button"
        >
          <List className="h-4 w-4" />
        </button>
        <button
          className={styles.toolbarButton}
          onClick={() => onFormatList('number')}
          title="Numbered List"
          type="button"
        >
          <ListOrdered className="h-4 w-4" />
        </button>
      </div>

      <div className={styles.divider} />

      <div className={styles.toolbarGroup}>
        <button
          className={`${styles.toolbarButton} ${
            alignment === 'left' ? styles.active : ''
          }`}
          onClick={() => onSetAlignment('left')}
          title="Align Left"
          type="button"
        >
          <AlignLeft className="h-4 w-4" />
        </button>
        <button
          className={`${styles.toolbarButton} ${
            alignment === 'center' ? styles.active : ''
          }`}
          onClick={() => onSetAlignment('center')}
          title="Align Center"
          type="button"
        >
          <AlignCenter className="h-4 w-4" />
        </button>
        <button
          className={`${styles.toolbarButton} ${
            alignment === 'right' ? styles.active : ''
          }`}
          onClick={() => onSetAlignment('right')}
          title="Align Right"
          type="button"
        >
          <AlignRight className="h-4 w-4" />
        </button>
        <button
          className={`${styles.toolbarButton} ${
            alignment === 'justify' ? styles.active : ''
          }`}
          onClick={() => onSetAlignment('justify')}
          title="Justify"
          type="button"
        >
          <AlignJustify className="h-4 w-4" />
        </button>
      </div>

      <div className={styles.divider} />

      <div className={styles.toolbarGroup}>
        <div className={styles.dropdown}>
          <button
            className={styles.toolbarButton}
            onClick={() => setShowColorPicker(!showColorPicker)}
            title="Text Color"
            type="button"
          >
            <div className={styles.colorButton}>
              <span>A</span>
              <div
                className={styles.colorIndicator}
                style={{ backgroundColor: textColor || '#000000' }}
              />
            </div>
          </button>
          {showColorPicker && (
            <div className={styles.colorPicker}>
              {colors.map((color) => (
                <button
                  key={color}
                  className={styles.colorSwatch}
                  style={{ backgroundColor: color }}
                  onClick={() => {
                    onSetColor(color)
                    setShowColorPicker(false)
                  }}
                  title={color}
                  type="button"
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.divider} />

      <div className={styles.toolbarGroup}>
        <button
          className={`${styles.toolbarButton} ${isLink ? styles.active : ''}`}
          onClick={onInsertLink}
          title="Insert Link"
          type="button"
        >
          <LinkIcon className="h-4 w-4" />
        </button>
      </div>

      <div className={styles.divider} />

      <div className={styles.toolbarGroup}>
        <button
          className={styles.toolbarButton}
          onClick={onInsertImage}
          title="Insert Image"
          type="button"
        >
          <ImageIcon className="h-4 w-4" />
        </button>
        <button
          className={styles.toolbarButton}
          onClick={onInsertVideo}
          title="Insert Video"
          type="button"
        >
          <Video className="h-4 w-4" />
        </button>
        <button
          className={styles.toolbarButton}
          onClick={onInsertYouTube}
          title="Insert YouTube Video"
          type="button"
        >
          <Youtube className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
